/**
 * Takes the enrolment code out of the URL, at boot, before anything
 * else can look at it.
 *
 * **Why this is not inside EnrolScreen, where it started.** It was, and
 * it only ran when that screen mounted — which only happens when the
 * device is *not* registered. Open an enrolment link in a browser that
 * is already registered and `api.me()` succeeds, the app goes straight
 * to the work screen, and the code sits in the address bar and in
 * history, unredeemed and unmentioned. That happened on 2026-09-07: an
 * admin opened a code on the phone the code was for, and the only sign
 * anything had gone wrong was the URL still reading `#code=…` minutes
 * later.
 *
 * So it runs at import, synchronously, on every path through the app.
 * The URL is clean whatever screen wins the race, and whoever wants the
 * code has to ask for it.
 *
 * A fragment never reaches the server, so this code has never been in a
 * request log — the address bar and history are the whole exposure, and
 * `replaceState` leaves no entry to go back to.
 */
function readAndClear(): string | null {
  if (typeof window === "undefined") return null;

  const hash = window.location.hash;
  if (!hash.includes("code=")) return null;

  const code = new URLSearchParams(hash.replace(/^#/, "")).get("code");
  // Cleared even when the value is empty or malformed: something that
  // looks like a credential should not stay on screen while somebody
  // works out whether it was one.
  window.history.replaceState(null, "", window.location.pathname);
  return code && code.length > 0 ? code : null;
}

let pending = readAndClear();

/** True if the app was opened with a code, whether or not it is still unused. */
export const arrivedWithCode = pending !== null;

/**
 * The code, once. Reading it consumes it, so a second render — or
 * StrictMode running an effect twice — cannot redeem a single-use code
 * a second time and report the failure over a success that already
 * happened.
 */
export function takeEnrolCode(): string | null {
  const code = pending;
  pending = null;
  return code;
}

/** Whether a code arrived and nothing has used it. */
export function enrolCodeUnused(): boolean {
  return pending !== null;
}
